import React, { useEffect } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  Package, Box, MessageSquare, Users, Settings, LogOut, Snowflake,
} from "lucide-react";
import { useAdminAuth } from "@/admin/AdminAuth";

const NAV = [
  { to: "/admin/orders", label: "Orders", icon: Package },
  { to: "/admin/products", label: "Products", icon: Box },
  { to: "/admin/messages", label: "Messages", icon: MessageSquare },
  { to: "/admin/subscribers", label: "Subscribers", icon: Users },
  { to: "/admin/settings", label: "Settings", icon: Settings },
];

export default function AdminLayout({ children, title }) {
  const { user, logout } = useAdminAuth();
  const nav = useNavigate();
  const loc = useLocation();

  // Guard: bounce to login if anon (once /me resolves)
  useEffect(() => {
    if (user === null) nav("/admin/login", { replace: true });
  }, [user, nav]);

  // Don't render children (which would fire their own authed API calls and
  // surface 401 promise-rejections in dev) until we know the user is signed in.
  if (user === undefined || user === null) {
    return (
      <div className="min-h-screen bg-ice-light flex items-center justify-center text-muted text-sm">
        Checking session…
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ice-light text-ink" data-testid="admin-shell">
      <header className="bg-navy-deep text-white border-b border-white/10">
        <div className="max-w-[1320px] mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center justify-center w-7 h-7 rounded-md bg-cyan/20 text-cyan">
              <Snowflake size={16} strokeWidth={2.2} />
            </span>
            <div className="leading-tight">
              <div className="text-sm font-bold tracking-tight">PeptiChill</div>
              <div className="text-[10px] uppercase tracking-widest text-white/55">Admin</div>
            </div>
          </div>
          <div className="flex items-center gap-3 text-xs text-white/70">
            <span data-testid="admin-username">{user?.username}</span>
            <button
              type="button"
              onClick={logout}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/8 hover:bg-white/15 transition-colors"
              data-testid="admin-logout"
            >
              <LogOut size={12} /> Sign out
            </button>
          </div>
        </div>
      </header>
      <div className="max-w-[1320px] mx-auto px-4 sm:px-6 grid md:grid-cols-[220px,1fr] gap-6 py-6">
        <aside className="md:sticky md:top-6 self-start">
          <nav className="flex md:flex-col gap-1 overflow-x-auto md:overflow-visible" data-testid="admin-nav">
            {NAV.map(({ to, label, icon: Icon }) => (
              <NavLink
                key={to}
                to={to}
                data-testid={`admin-nav-${label.toLowerCase()}`}
                className={({ isActive }) =>
                  `inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                    isActive
                      ? "bg-white text-navy shadow-soft"
                      : "text-ink/70 hover:bg-white/60 hover:text-navy"
                  }`
                }
              >
                <Icon size={16} strokeWidth={1.9} />
                {label}
              </NavLink>
            ))}
          </nav>
        </aside>
        <main className="min-w-0">
          {title && (
            <h1 className="text-2xl sm:text-3xl font-bold text-navy tracking-tight mb-6">
              {title}
            </h1>
          )}
          {children}
        </main>
      </div>
    </div>
  );
}
